import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    ArrowRight,
    BookOpen,
    ExternalLink,
    ChevronDown,
    ChevronUp,
    Link2Icon,
} from "lucide-react";
import Navbar from "../../../../components/navbar/Navbar";
import Footer from "../../../../components/footer/Footer";
import WhatsappPanel from "../../../../components/whatsapp-panel/WhatsappPanel";
import { courseApi } from "../../../../api/api";
import api from "../../../../utils/api";
import CallToActionBanner from "../../home/call-to-action-banner/CallToActionBanner";

const CoursePage = () => {
    const { id } = useParams();
    const [course, setCourse] = useState(null);
    const [loading, setLoading] = useState(true);
    const [expandedSections, setExpandedSections] = useState({});
    const [expandedLinks, setExpandedLinks] = useState({});

    const sessionData = sessionStorage.getItem("studentSession");
    const hasSession = !!sessionData;
    const redirectUrl = hasSession ? "/dashboard" : "/login";

    useEffect(() => {
        setLoading(true);
        api.get(`${courseApi}/${id}`)
            .then((res) => res.data)
            .then((data) => {
                const mappedData = {
                    id: data.id,
                    title: data.title,
                    shortDescription: data.short_description,
                    description: data.long_description,
                    image: data.image,
                    eligibility: {
                        title: data.eligibility_title,
                        shortDescription: data.eligibility_short_description,
                        longDescription: data.eligibility_long_description,
                        image: data.eligibility_image,
                        usefulLinks: [
                            {
                                name: data.eligibility_useful_link1_title,
                                url: data.eligibility_useful_link1,
                            },
                            {
                                name: data.eligibility_useful_link2_title,
                                url: data.eligibility_useful_link2,
                            },
                            {
                                name: data.eligibility_useful_link3_title,
                                url: data.eligibility_useful_link3,
                            },
                            {
                                name: data.eligibility_useful_link4_title,
                                url: data.eligibility_useful_link4,
                            },
                        ].filter((l) => l && l.name && l.url),
                    },
                    examPattern: {
                        title: data.exampattern_title,
                        shortDescription: data.exampattern_short_description,
                        longDescription: data.exampattern_long_description,
                        image: data.exampattern_image,
                        usefulLinks: [
                            {
                                name: data.exampattern_useful_link1_title,
                                url: data.exampattern_useful_link1,
                            },
                            {
                                name: data.exampattern_useful_link2_title,
                                url: data.exampattern_useful_link2,
                            },
                            {
                                name: data.exampattern_useful_link3_title,
                                url: data.exampattern_useful_link3,
                            },
                            {
                                name: data.exampattern_useful_link4_title,
                                url: data.exampattern_useful_link4,
                            },
                        ].filter((l) => l && l.name && l.url),
                    },
                    syllabus: {
                        title: data.syllabus_title,
                        shortDescription: data.syllabus_short_description,
                        longDescription: data.syllabus_long_description,
                        image: data.syllabus_image,
                        usefulLinks: [
                            {
                                name: data.syllabus_useful_link1_title,
                                url: data.syllabus_useful_link1,
                            },
                            {
                                name: data.syllabus_useful_link2_title,
                                url: data.syllabus_useful_link2,
                            },
                            {
                                name: data.syllabus_useful_link3_title,
                                url: data.syllabus_useful_link3,
                            },
                            {
                                name: data.syllabus_useful_link4_title,
                                url: data.syllabus_useful_link4,
                            },
                        ].filter((l) => l && l.name && l.url),
                    },
                    importantDates: {
                        title: data.important_dates_title,
                        shortDescription:
                            data.important_dates_short_description,
                        longDescription: data.important_dates_long_description,
                        image: data.important_dates_image,
                        usefulLinks: [
                            {
                                name: data.important_dates_useful_link1_title,
                                url: data.important_dates_useful_link1,
                            },
                            {
                                name: data.important_dates_useful_link2_title,
                                url: data.important_dates_useful_link2,
                            },
                            {
                                name: data.important_dates_useful_link3_title,
                                url: data.important_dates_useful_link3,
                            },
                            {
                                name: data.important_dates_useful_link4_title,
                                url: data.important_dates_useful_link4,
                            },
                        ].filter((l) => l && l.name && l.url),
                    },
                    fees: {
                        title: data.fees_title,
                        shortDescription: data.fees_short_description,
                        longDescription: data.fees_long_description,
                        image: data.fees_image,
                        usefulLinks: [
                            {
                                name: data.fees_useful_link1_title,
                                url: data.fees_useful_link1,
                            },
                            {
                                name: data.fees_useful_link2_title,
                                url: data.fees_useful_link2,
                            },
                            {
                                name: data.fees_useful_link3_title,
                                url: data.fees_useful_link3,
                            },
                            {
                                name: data.fees_useful_link4_title,
                                url: data.fees_useful_link4,
                            },
                        ].filter((l) => l && l.name && l.url),
                    },
                    contact: {
                        title: data.contact_title,
                        shortDescription: data.contact_short_description,
                        longDescription: data.contact_long_description,
                        image: data.contact_image,
                        usefulLinks: [
                            {
                                name: data.contact_useful_link1_title,
                                url: data.contact_useful_link1,
                            },
                            {
                                name: data.contact_useful_link2_title,
                                url: data.contact_useful_link2,
                            },
                            {
                                name: data.contact_useful_link3_title,
                                url: data.contact_useful_link3,
                            },
                            {
                                name: data.contact_useful_link4_title,
                                url: data.contact_useful_link4,
                            },
                        ].filter((l) => l && l.name && l.url),
                    },
                };
                setCourse(mappedData);
            })
            .catch((err) => console.error(err))
            .finally(() => setLoading(false));
    }, [id]);

    const toggleReadMore = (index) => {
        setExpandedSections((prev) => ({ ...prev, [index]: !prev[index] }));
    };

    const toggleLinksDropdown = (index) => {
        setExpandedLinks((prev) => ({ ...prev, [index]: !prev[index] }));
    };

    const handleOpenLink = (url) => {
        if (!url) return;
        const isValidUrl =
            url.startsWith("http://") ||
            url.startsWith("https://") ||
            url.includes(".");
        const finalUrl = isValidUrl
            ? url.startsWith("http://") || url.startsWith("https://")
                ? url
                : `https://${url}`
            : `https://www.google.com/search?q=${encodeURIComponent(url)}`;
        window.open(finalUrl, "_blank", "noopener,noreferrer");
    };

    // Strict animation variants matching your theme
    const cardVariants = {
        hidden: { opacity: 0, y: 15 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.4, ease: "easeOut" },
        },
    };

    const renderSection = (section, sectionName, index) => {
        if (!section) return null;

        const hasContent =
            section.title ||
            section.shortDescription ||
            section.longDescription ||
            section.image ||
            (section.usefulLinks && section.usefulLinks.length > 0);

        if (!hasContent) return null;

        const isEven = index % 2 === 0;
        const longText = section.longDescription || "";
        const words = longText.split(/\s+/);
        const isLong = words.length > 25;
        const previewText = words.slice(0, 25).join(" ");
        const validLinks = section.usefulLinks || [];

        return (
            <motion.section
                key={index}
                variants={cardVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                className={`py-24 px-4 sm:px-6 lg:px-8 w-full border-t border-gray-100 ${isEven ? "bg-white" : "bg-gray-50"}`}
            >
                <div
                    className={`max-w-7xl mx-auto flex flex-col ${isEven ? "lg:flex-row" : "lg:flex-row-reverse"} gap-12 items-start`}
                >
                    {/* Content Column */}
                    <div
                        className={`w-full ${section.image ? "lg:w-1/2" : "max-w-4xl mx-auto"} flex flex-col order-2 lg:order-none`}
                    >
                        <div className="flex items-center gap-2 text-red-700 font-bold text-xs uppercase mb-3">
                            <span className="w-6 h-1 bg-red-700"></span>
                            {sectionName}
                        </div>

                        {section.title && (
                            <h3 className="text-3xl font-black text-neutral-900 mb-4">
                                {section.title}
                            </h3>
                        )}

                        {section.shortDescription && (
                            <p className="text-gray-600 text-base font-medium mb-6">
                                {section.shortDescription}
                            </p>
                        )}

                        {longText && (
                            <div className="text-gray-600 text-sm leading-relaxed mb-6">
                                {expandedSections[index] ? (
                                    <>
                                        <div
                                            dangerouslySetInnerHTML={{
                                                __html: longText,
                                            }}
                                            className="prose prose-sm max-w-none text-gray-600"
                                        />
                                        {isLong && (
                                            <button
                                                onClick={() =>
                                                    toggleReadMore(index)
                                                }
                                                className="mt-4 inline-flex items-center text-red-700 font-bold text-sm hover:text-neutral-900 transition-colors"
                                            >
                                                Read Less{" "}
                                                <ChevronUp
                                                    size={16}
                                                    className="ml-1"
                                                />
                                            </button>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <span
                                            dangerouslySetInnerHTML={{
                                                __html: previewText.replace(
                                                    /<[^>]*>/g,
                                                    "",
                                                ),
                                            }}
                                        />
                                        {isLong && (
                                            <>
                                                ...{" "}
                                                <button
                                                    onClick={() =>
                                                        toggleReadMore(index)
                                                    }
                                                    className="mt-4 inline-flex items-center text-red-700 font-bold text-sm hover:text-neutral-900 transition-colors"
                                                >
                                                    Read More{" "}
                                                    <ChevronDown
                                                        size={16}
                                                        className="ml-1"
                                                    />
                                                </button>
                                            </>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        {/* Resource Tags (Matches the upcoming batches tag style) */}
                        {validLinks.length > 0 && (
                            <div className="mt-auto pt-4 border-t border-gray-100">
                                <h4 className="text-xs font-bold text-neutral-900 mb-4 uppercase tracking-widest flex items-center">
                                    <Link2Icon
                                        size={14}
                                        className="mr-2 text-red-700"
                                    />
                                    Essential Resources
                                </h4>
                                <div className="flex flex-wrap gap-2.5">
                                    {validLinks
                                        .slice(
                                            0,
                                            expandedLinks[index]
                                                ? validLinks.length
                                                : 4,
                                        )
                                        .map((link, i) => (
                                            <button
                                                key={i}
                                                onClick={() =>
                                                    handleOpenLink(link.url)
                                                }
                                                className="inline-flex items-center px-3 py-1.5 bg-white border border-gray-200 text-neutral-900 rounded shadow-sm hover:border-neutral-900 hover:shadow-md transition-all text-xs font-bold group"
                                            >
                                                {link.name}
                                                <ExternalLink
                                                    size={12}
                                                    className="ml-2 text-gray-400 group-hover:text-red-700 transition-colors"
                                                />
                                            </button>
                                        ))}
                                </div>

                                {validLinks.length > 4 && (
                                    <button
                                        className="mt-4 text-xs font-bold text-gray-500 hover:text-neutral-900 transition-colors flex items-center"
                                        onClick={() =>
                                            toggleLinksDropdown(index)
                                        }
                                    >
                                        {expandedLinks[index]
                                            ? "- Hide Additional Links"
                                            : `+ View ${validLinks.length - 4} More Links`}
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Image Column */}
                    {section.image && (
                        <div className="w-full lg:w-1/2 aspect-video relative overflow-hidden rounded-xl border border-gray-200 shadow-sm bg-gray-100 group order-1 lg:order-none mb-6 lg:mb-0">
                            <img
                                src={section.image}
                                alt={sectionName}
                                onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.style.objectFit = "contain";
                                    e.target.style.backgroundColor = "#f0f0f0";
                                    e.target.style.filter = "grayscale(100%)";
                                    e.target.style.opacity = "0.5";
                                    e.target.src = "/assets/logo.png";
                                }}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-in-out"
                            />
                            <div className="absolute inset-0 bg-neutral-900/0 group-hover:bg-neutral-900/10 transition-colors duration-300"></div>
                        </div>
                    )}
                </div>
            </motion.section>
        );
    };

    if (loading || !course) {
        return (
            <>
                <div className="min-h-screen py-24 px-4 sm:px-6 lg:px-8 bg-white flex justify-center">
                    <div className="w-full max-w-7xl animate-pulse flex flex-col gap-12">
                        {/* Structural Skeleton */}
                        <div className="flex flex-col lg:flex-row gap-8">
                            <div className="flex-1 space-y-4 pt-10">
                                <div className="h-4 bg-gray-200 rounded w-24"></div>
                                <div className="h-10 bg-gray-200 rounded w-3/4"></div>
                                <div className="h-4 bg-gray-100 rounded w-full"></div>
                                <div className="h-4 bg-gray-100 rounded w-5/6"></div>
                                <div className="h-12 bg-gray-200 rounded-lg w-40 mt-8"></div>
                            </div>
                            <div className="flex-1 aspect-video bg-gray-200 rounded-xl"></div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    const sections = [
        { data: course.eligibility, name: "Eligibility" },
        { data: course.examPattern, name: "Exam Pattern" },
        { data: course.syllabus, name: "Syllabus" },
        { data: course.importantDates, name: "Important Dates" },
        { data: course.fees, name: "Fees" },
        { data: course.contact, name: "Contact" },
    ];

    return (
        <div className="bg-white min-h-screen">
            {/* Hero Section */}
            <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white border-b border-gray-100 w-full relative">
                <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-12">
                    <div className="flex-1 flex flex-col z-10">
                        <div className="flex items-center gap-2 text-red-700 font-bold text-xs uppercase mb-3">
                            <span className="w-6 h-1 bg-red-700"></span>
                            Course Overview
                        </div>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-neutral-900 mb-4 leading-tight">
                            {course.title.split(" ").map((word, i, arr) =>
                                i === arr.length - 1 ? (
                                    <span key={i} className="text-yellow-500">
                                        {word}
                                    </span>
                                ) : (
                                    word + " "
                                ),
                            )}
                        </h1>
                        <p className="text-gray-600 text-base font-medium mb-6 leading-relaxed">
                            {course.shortDescription}
                        </p>

                        {course.description && (
                            <div
                                className="prose prose-sm text-gray-600 mb-8"
                                dangerouslySetInnerHTML={{
                                    __html: course.description,
                                }}
                            />
                        )}

                        <div className="flex flex-col gap-4 mt-auto items-start">
                            <Link
                                to={redirectUrl}
                                onClick={() =>
                                    sessionStorage.setItem(
                                        "redirectFrom",
                                        window.location.pathname,
                                    )
                                }
                                className="inline-flex whitespace-nowrap h-fit items-center justify-center px-6 py-3 bg-neutral-900 hover:bg-red-700 text-white font-bold rounded-lg shadow-sm transition-colors duration-300 group"
                            >
                                Enroll Now
                                <ArrowRight
                                    size={16}
                                    className="ml-2 group-hover:translate-x-1 transition-transform"
                                />
                            </Link>
                        </div>
                    </div>

                    <div className="flex-1 w-full">
                        <div className="w-full aspect-video relative rounded-xl overflow-hidden shadow-sm border border-gray-200 bg-gray-100 group">
                            {course.image ? (
                                <img
                                    src={course.image}
                                    alt={course.title}
                                    onError={(e) => {
                                        e.target.onerror = null;
                                        e.target.style.objectFit = "contain";
                                        e.target.style.backgroundColor =
                                            "#f0f0f0";
                                        e.target.style.filter =
                                            "grayscale(100%)";
                                        e.target.style.opacity = "0.5";
                                        e.target.src = "/assets/logo.png";
                                    }}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-in-out"
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                    <BookOpen
                                        size={48}
                                        className="text-gray-300"
                                    />
                                </div>
                            )}

                            <div className="absolute inset-0 bg-neutral-900/0 group-hover:bg-neutral-900/10 transition-colors duration-300"></div>
                        </div>
                        <div className="mt-6 flex justify-center p-5 bg-gray-100 rounded-2xl">
                            <WhatsappPanel />
                        </div>
                    </div>
                </div>
            </section>

            {/* Content Sections */}
            <div className="flex flex-col w-full">
                {sections.map((sec, idx) =>
                    renderSection(sec.data, sec.name, idx),
                )}
            </div>

            <CallToActionBanner />
        </div>
    );
};

export default CoursePage;
